import React, { Fragment, useState, useEffect, useCallback, useRef } from "react";
import { Combobox } from "@headlessui/react";
import { Search, Box, Table2 } from "lucide-react";
import type { DataSource, DataSourceType } from "../types/imports";
import { fetchSources, ApiRequestError } from "../services/api";

interface CommandPaletteProps {
  open: boolean;
  onClose: () => void;
  orgId: string | null;
  onSelect: (source: DataSource) => void;
  onAuthExpired?: () => void;
  recentSourceIds?: string[];
}

function SourceIcon({ type }: { type: DataSourceType }) {
  return (
    <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-bg flex-shrink-0">
      {type === "model" ? (
        <Box size={16} className="text-fg2" />
      ) : (
        <Table2 size={16} className="text-fg2" />
      )}
    </div>
  );
}

export function CommandPalette({
  open,
  onClose,
  orgId,
  onSelect,
  onAuthExpired,
  recentSourceIds = [],
}: CommandPaletteProps) {
  const [query, setQuery] = useState("");
  const [allSources, setAllSources] = useState<DataSource[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const loadSources = useCallback(
    async (search?: string) => {
      if (!orgId) return;
      setLoading(true);
      setError(null);
      try {
        const sources = await fetchSources(orgId, search || undefined);
        setAllSources(sources);
      } catch (err) {
        if (err instanceof ApiRequestError && err.status === 401) {
          onAuthExpired?.();
          onClose();
          return;
        }
        setError("Failed to load data sources");
      } finally {
        setLoading(false);
      }
    },
    [orgId, onAuthExpired, onClose],
  );

  // Load all sources when palette opens
  useEffect(() => {
    if (open && orgId) {
      setQuery("");
      loadSources();
      // Focus input after a tick
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open, orgId, loadSources]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [open, onClose]);

  // Debounced search
  const handleQueryChange = (value: string) => {
    setQuery(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      loadSources(value);
    }, 300);
  };

  // Filter locally for instant feedback
  const filtered = query
    ? allSources.filter(
        (s) =>
          s.name.toLowerCase().includes(query.toLowerCase()) ||
          (s.collection || "").toLowerCase().includes(query.toLowerCase()),
      )
    : allSources;

  // Split into recent and rest
  const recentSet = new Set(recentSourceIds);
  const recentSources = filtered.filter((s) => recentSet.has(s.id));
  const otherSources = filtered.filter((s) => !recentSet.has(s.id));

  const handleSelect = (source: DataSource | null) => {
    if (source) {
      onSelect(source);
      onClose();
    }
  };

  if (!open) return null;

  return (
    <div className="absolute inset-0 z-50 flex flex-col">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-fg/25"
        onClick={onClose}
      />

      {/* Palette */}
      <div className="relative z-10 mx-3 mt-14 transform overflow-hidden rounded-xl bg-bg-elevated shadow-2xl ring-1 ring-bg3">
        <Combobox value={null} onChange={handleSelect}>
          <div className="relative">
            <Search
              className="pointer-events-none absolute left-4 top-3.5 text-fg3"
              size={18}
            />
            <Combobox.Input
              ref={inputRef}
              className="h-12 w-full border-0 bg-transparent pl-11 pr-4 text-fg placeholder:text-fg3 text-[14px] font-display-body focus:ring-0 focus:outline-none"
              placeholder="Search Cashboard data..."
              onChange={(e) => handleQueryChange(e.target.value)}
              value={query}
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck={false}
            />
          </div>

          {(loading && allSources.length === 0) ? (
            <div className="px-4 py-8 text-center text-[13px] text-fg2">
              Loading sources...
            </div>
          ) : error ? (
            <div className="px-4 py-8 text-center text-[13px] text-spark">
              {error}
            </div>
          ) : filtered.length === 0 ? (
            <div className="px-4 py-8 text-center text-[13px] text-fg2">
              {query ? "No results found." : "No data sources available."}
            </div>
          ) : (
            <Combobox.Options static className="max-h-80 scroll-py-2 overflow-y-auto p-2">
              {recentSources.length > 0 && !query && (
                <>
                  <div className="px-2 pt-1 pb-1.5">
                    <span className="text-[11px] font-medium text-fg3 uppercase tracking-wider">
                      Recently imported
                    </span>
                  </div>
                  {recentSources.map((source) => (
                    <Combobox.Option
                      key={`recent-${source.id}`}
                      value={source}
                      className={({ active }) =>
                        `flex items-center gap-3 rounded-md px-2 py-2 cursor-pointer transition-colors ${active ? "bg-bg2" : ""}`
                      }
                    >
                      <SourceIcon type={source.type} />
                      <div className="min-w-0 flex-1">
                        <p className="text-[13px] text-fg truncate">{source.name}</p>
                        {source.collection && (
                          <p className="text-[11px] text-fg2 truncate">{source.collection}</p>
                        )}
                      </div>
                    </Combobox.Option>
                  ))}
                </>
              )}

              {otherSources.length > 0 && (
                <>
                  {(recentSources.length > 0 && !query) && (
                    <div className="px-2 pt-3 pb-1.5">
                      <span className="text-[11px] font-medium text-fg3 uppercase tracking-wider">
                        All sources
                      </span>
                    </div>
                  )}
                  {(recentSources.length === 0 || query) && (
                    <div className="px-2 pt-1 pb-1.5">
                      <span className="text-[11px] font-medium text-fg3 uppercase tracking-wider">
                        {query ? "Results" : "All sources"}
                      </span>
                    </div>
                  )}
                  {otherSources.map((source) => (
                    <Combobox.Option
                      key={source.id}
                      value={source}
                      className={({ active }) =>
                        `flex items-center gap-3 rounded-md px-2 py-2 cursor-pointer transition-colors ${active ? "bg-bg2" : ""}`
                      }
                    >
                      <SourceIcon type={source.type} />
                      <div className="min-w-0 flex-1">
                        <p className="text-[13px] text-fg truncate">{source.name}</p>
                        {source.collection && (
                          <p className="text-[11px] text-fg2 truncate">{source.collection}</p>
                        )}
                      </div>
                    </Combobox.Option>
                  ))}
                </>
              )}
            </Combobox.Options>
          )}

          <div className="border-t border-bg3 px-4 py-2.5">
            <p className="text-[11px] text-fg3">
              <kbd className="font-sans">↑↓</kbd> to navigate · <kbd className="font-sans">↵</kbd> to select · <kbd className="font-sans">esc</kbd> to close
            </p>
          </div>
        </Combobox>
      </div>
    </div>
  );
}
